import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDayOfWeek } from "./enums/enumDayOfWeek.generated";
import { EnumGetUserByCredentialStatus } from "./enums/enumGetUserByCredentialStatus.generated";
import { EnumUserPermission } from "./enums/enumUserPermission.generated";
import { EnumUserTenantGroupLevel } from "./enums/enumUserTenantGroupLevel.generated";

// Model Objects

export class UserReadOnly extends ModelObject<UserReadOnly> {

    /**
    * When the password was last changed
    */
    public passwordLastChanged: Instant = Instant.ofEpochMilli(0);
    /**
    * True, if this user is an internal system user
    */
    public isSystemUser: boolean = false;
    /**
    * True, if this user is a support user
    */
    public isSupportUser: boolean = false;
    private _selectedTenant: Guid | null = null;
    /**
    * The tenant within the tenant group the user has selected to view
    */
    public get selectedTenant(): Guid | null { return this._selectedTenant }
    public set selectedTenant(value: Guid | null) {
      this._selectedTenant = value === undefined ? null : value;
    }
    /**
    * Tenant group id
    */
    public tenantGroupId: Guid = Guid.createEmpty();
    /**
    * True, if this user has verified their email address, often through an external identity provider. (Updated by system).
    */
    public isEmailVerified: boolean = false;
    private _lastLogonAt: Instant | null = null;
    /**
    * Timestamp of last logon (updated by system).
    */
    public get lastLogonAt(): Instant | null { return this._lastLogonAt }
    public set lastLogonAt(value: Instant | null) {
      this._lastLogonAt = value === undefined ? null : value;
    }
    private _invitedAt: Instant | null = null;
    /**
    * Timestamp of invitation (if any) to join the tenant group (updated by system).
    */
    public get invitedAt(): Instant | null { return this._invitedAt }
    public set invitedAt(value: Instant | null) {
      this._invitedAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.passwordLastChanged = Instant.parse(jsonSerializedObject.passwordLastChanged);
          this.isSystemUser = jsonSerializedObject.isSystemUser;
          this.isSupportUser = jsonSerializedObject.isSupportUser;
          this.selectedTenant = jsonSerializedObject.selectedTenant == null ? null : Guid.parseOrEmpty(jsonSerializedObject.selectedTenant);
          this.tenantGroupId = Guid.parseOrEmpty(jsonSerializedObject.tenantGroupId);
          this.isEmailVerified = jsonSerializedObject.isEmailVerified;
          this.lastLogonAt = jsonSerializedObject.lastLogonAt == null ? null : Instant.parse(jsonSerializedObject.lastLogonAt);
          this.invitedAt = jsonSerializedObject.invitedAt == null ? null : Instant.parse(jsonSerializedObject.invitedAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.passwordLastChanged = this.passwordLastChanged.toString();
        toRet.isSystemUser = this.isSystemUser;
        toRet.isSupportUser = this.isSupportUser;
        toRet.selectedTenant = this.selectedTenant == null ? null : this.selectedTenant.toString();
        toRet.tenantGroupId = this.tenantGroupId.toString();
        toRet.isEmailVerified = this.isEmailVerified;
        toRet.lastLogonAt = this.lastLogonAt == null ? null : this.lastLogonAt.toString();
        toRet.invitedAt = this.invitedAt == null ? null : this.invitedAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "selectedTenant", rule: (v: any) => v == null || !v.isEmpty() || "UserReadOnly.SelectedTenant cannot be empty"},
            {field: "tenantGroupId", rule: (v: any) => !v.isEmpty() || "UserReadOnly.TenantGroupId cannot be empty"},
    ];
}


export class UserCalculated extends ModelObject<UserCalculated> {

    public hasApiSecurityToken: boolean = false;
    public hasGipId: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.hasApiSecurityToken = jsonSerializedObject.hasApiSecurityToken;
          this.hasGipId = jsonSerializedObject.hasGipId;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.hasApiSecurityToken = this.hasApiSecurityToken;
        toRet.hasGipId = this.hasGipId;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class User extends ModelObject<User> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * User email and login ID
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public email: string = "";
    /**
    * The users name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Job title in the company
    * Not Nullable
    * Max Length : 1000
    */
    public jobTitle: string = "";
    /**
    * The permissions level of the current user on the users tenant group
    */
    public userTenantGroupLevel: EnumUserTenantGroupLevel = EnumUserTenantGroupLevel.member;
    /**
    * True, if this user is disabled (i.e. cannot log in)
    */
    public isDisabled: boolean = false;
    public isHidden: boolean = false;
    /**
    * The preferred locale for translating language for this user, Eg 'en-us'. If empty, then the system default (english) will be used.
    * Not Nullable
    * Max Length : 1000
    */
    public languageLocale: string = "";
    /**
    * Start day of all calendars in the system.
    */
    public calendarStartDay: EnumDayOfWeek = EnumDayOfWeek.none;
    /**
    * If true, then sessions for this user are locked and cannot be kicked by other users.
    */
    public lockSessions: boolean = false;
    public readOnly: UserReadOnly = new UserReadOnly();
    public calculated: UserCalculated = new UserCalculated();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.email = jsonSerializedObject.email;
          this.name = jsonSerializedObject.name;
          this.jobTitle = jsonSerializedObject.jobTitle;
          this.userTenantGroupLevel = EnumUserTenantGroupLevel.getByValue(jsonSerializedObject.userTenantGroupLevel, this.userTenantGroupLevel);
          this.isDisabled = jsonSerializedObject.isDisabled;
          this.isHidden = jsonSerializedObject.isHidden;
          this.languageLocale = jsonSerializedObject.languageLocale;
          this.calendarStartDay = EnumDayOfWeek.getByValue(jsonSerializedObject.calendarStartDay, this.calendarStartDay);
          this.lockSessions = jsonSerializedObject.lockSessions;
          this.readOnly = new UserReadOnly(jsonSerializedObject.readOnly);
          this.calculated = new UserCalculated(jsonSerializedObject.calculated);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.email = this.email;
        toRet.name = this.name;
        toRet.jobTitle = this.jobTitle;
        toRet.userTenantGroupLevel = this.userTenantGroupLevel.value;
        toRet.isDisabled = this.isDisabled;
        toRet.isHidden = this.isHidden;
        toRet.languageLocale = this.languageLocale;
        toRet.calendarStartDay = this.calendarStartDay.value;
        toRet.lockSessions = this.lockSessions;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.calculated = this.calculated.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "User.Id cannot be empty"},
            {field: "email", rule: (v: any) => v == null || v.length !== 0 || "Email cannot be empty"},
            {field: "email", rule: (v: any) => v == null || v.length <= 1000 || "Email has too many characters (1000)"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "jobTitle", rule: (v: any) => v == null || v.length <= 1000 || "JobTitle has too many characters (1000)"},
            {field: "languageLocale", rule: (v: any) => v == null || v.length <= 1000 || "LanguageLocale has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
    ];
}


export class UserContext extends ModelObject<UserContext> {

    public user: User = new User();
    public tenantGroupId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.user = new User(jsonSerializedObject.user);
          this.tenantGroupId = Guid.parseOrEmpty(jsonSerializedObject.tenantGroupId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.user = this.user.toJsonSerializedObject();
        toRet.tenantGroupId = this.tenantGroupId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "user", rule: (v: any) => !!v || "user is required"},
            {field: "user", rule: (v: any) => v.validate()},
            {field: "tenantGroupId", rule: (v: any) => !v.isEmpty() || "UserContext.TenantGroupId cannot be empty"},
    ];
}


export class UserFromCredentials extends ModelObject<UserFromCredentials> {

    public status: EnumGetUserByCredentialStatus = EnumGetUserByCredentialStatus.ok;
    private _user: User | null = null;
    public get user(): User | null { return this._user }
    public set user(value: User | null) {
      this._user = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.status = EnumGetUserByCredentialStatus.getByValue(jsonSerializedObject.status, this.status);
          this.user = jsonSerializedObject.user == null ? null : new User(jsonSerializedObject.user);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.status = this.status.value;
        toRet.user = this.user == null ? null : this.user?.toJsonSerializedObject() ?? null;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "user", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class UserWithGroupDetails extends ModelObject<UserWithGroupDetails> {

    public user: User = new User();
    public tenantGroupName: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.user = new User(jsonSerializedObject.user);
          this.tenantGroupName = jsonSerializedObject.tenantGroupName;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.user = this.user.toJsonSerializedObject();
        toRet.tenantGroupName = this.tenantGroupName;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "user", rule: (v: any) => !!v || "user is required"},
            {field: "user", rule: (v: any) => v.validate()},
    ];
}


export class UserRole extends ModelObject<UserRole> {

    /**
    * User which has the role.
    */
    public userId: Guid = Guid.createEmpty();
    /**
    * Role granted to the user - within the given tenant.
    */
    public roleId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.roleId = Guid.parseOrEmpty(jsonSerializedObject.roleId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.userId = this.userId.toString();
        toRet.roleId = this.roleId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "userId", rule: (v: any) => !v.isEmpty() || "UserRole.UserId cannot be empty"},
            {field: "roleId", rule: (v: any) => !v.isEmpty() || "UserRole.RoleId cannot be empty"},
    ];
}


export class UserFavouriteMenu extends ModelObject<UserFavouriteMenu> {

    public id: Guid = Guid.createEmpty();
    public displayAs: string = "";
    public menuIdentifier: string = "";
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.displayAs = jsonSerializedObject.displayAs;
          this.menuIdentifier = jsonSerializedObject.menuIdentifier;
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.displayAs = this.displayAs;
        toRet.menuIdentifier = this.menuIdentifier;
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "UserFavouriteMenu.Id cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class UserService extends ServiceBase {

  public async getCurrentUserContext(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<UserContext> {
    const req = {
    };
    const extractor = (response:any) => new UserContext(response.userContext);;
    return this.makeJsonRequest("user/v1/getCurrentUserContext", req, false, extractor, opts);
  };

  public async getAllUsers(tenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(User)[]> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    };
    const extractor = (response:any) => response.users.map((v: any) => new User(v));;
    return this.makeJsonRequest("user/v1/getAllUsers", req, false, extractor, opts);
  };

  public async getUser(tenantGroupId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<User> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    };
    const extractor = (response:any) => new User(response.user);;
    return this.makeJsonRequest("user/v1/getUser", req, false, extractor, opts);
  };

  public async getUsers(tenantGroupId: Guid, userIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(User)[]> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userIds:userIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.users.map((v: any) => new User(v));;
    return this.makeJsonRequest("user/v1/getUsers", req, false, extractor, opts);
  };

  public async createUser(tenantGroupId: Guid, user: User, password: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    user:user.toJsonSerializedObject(),
    password:password,
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/createUser", req, true, extractor, opts);
  };

  public async updateUser(tenantGroupId: Guid, expectedVersion: number | null, user: User, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    expectedVersion:expectedVersion == null ? null : expectedVersion,
    user:user.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/updateUser", req, true, extractor, opts);
  };

  public async changePassword(tenantGroupId: Guid, userId: Guid, newPassword: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    newPassword:newPassword,
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/changePassword", req, false, extractor, opts);
  };

  public async setUserIsHidden(tenantGroupId: Guid, userId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/setUserIsHidden", req, true, extractor, opts);
  };

  public async setUserIsDisabled(tenantGroupId: Guid, userId: Guid, disable: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    disable:disable,
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/setUserIsDisabled", req, true, extractor, opts);
  };

  public async requestPasswordReset(accountName: string, userEmailAddress: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    accountName:accountName,
    userEmailAddress:userEmailAddress,
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/requestPasswordReset", req, false, extractor, opts);
  };

  public async resetUserPassword(resetToken: Guid, newPassword: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    resetToken:resetToken.toString(),
    newPassword:newPassword,
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/resetUserPassword", req, false, extractor, opts);
  };

  public async updateSelectedTenantId(tenantGroupId: Guid, userId: Guid, selectedTenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    selectedTenantId:selectedTenantId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/updateSelectedTenantId", req, true, extractor, opts);
  };

  public async updateSelectedLanguageLocale(tenantGroupId: Guid, languageLocale: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    languageLocale:languageLocale,
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/updateSelectedLanguageLocale", req, true, extractor, opts);
  };

  public async getApiSecurityToken(tenantGroupId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    };
    const extractor = (response:any) => response.apiSecurityToken;;
    return this.makeJsonRequest("user/v1/getApiSecurityToken", req, false, extractor, opts);
  };

  public async generateNewApiSecurityToken(tenantGroupId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/generateNewApiSecurityToken", req, true, extractor, opts);
  };

  public async removeApiSecurityToken(tenantGroupId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/removeApiSecurityToken", req, true, extractor, opts);
  };

  public async loginWithGipIdToken(tenantGroupId: Guid, gipIdToken: string, browserInstanceId: string | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    gipIdToken:gipIdToken,
    browserInstanceId:browserInstanceId == null ? null : browserInstanceId,
    };
    const extractor = (response:any) => response.sessionToken;;
    return this.makeJsonRequest("user/v1/loginWithGipIdToken", req, false, extractor, opts);
  };

  public async loginSupportUserWithGipIdToken(tenantGroupName: string, gipIdToken: string, browserInstanceId: string | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantGroupName:tenantGroupName,
    gipIdToken:gipIdToken,
    browserInstanceId:browserInstanceId == null ? null : browserInstanceId,
    };
    const extractor = (response:any) => response.sessionToken;;
    return this.makeJsonRequest("user/v1/loginSupportUserWithGipIdToken", req, false, extractor, opts);
  };

  public async findUsersMatchingGipIdToken(gipIdToken: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(UserWithGroupDetails)[]> {
    const req = {
    gipIdToken:gipIdToken,
    };
    const extractor = (response:any) => response.results.map((v: any) => new UserWithGroupDetails(v));;
    return this.makeJsonRequest("user/v1/findUsersMatchingGipIdToken", req, false, extractor, opts);
  };

  public async sendInvitationEmail(tenantGroupId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/sendInvitationEmail", req, false, extractor, opts);
  };

  public async inviteNewUser(tenantGroupId: Guid, email: string, name: string, jobTitle: string, userTenantGroupLevel: EnumUserTenantGroupLevel, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    email:email,
    name:name,
    jobTitle:jobTitle,
    userTenantGroupLevel:userTenantGroupLevel.value,
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/inviteNewUser", req, true, extractor, opts);
  };

  public async getRolesForUser(tenantId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Guid)[]> {
    const req = {
    tenantId:tenantId.toString(),
    userId:userId.toString(),
    };
    const extractor = (response:any) => response.roles.map((v: any) => Guid.parseOrEmpty(v));;
    return this.makeJsonRequest("user/v1/getRolesForUser", req, false, extractor, opts);
  };

  public async saveRolesForUser(tenantId: Guid, userId: Guid, roles: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    userId:userId.toString(),
    roles:roles.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/saveRolesForUser", req, true, extractor, opts);
  };

  public async getAllUserRoles(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(UserRole)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.userRoles.map((v: any) => new UserRole(v));;
    return this.makeJsonRequest("user/v1/getAllUserRoles", req, false, extractor, opts);
  };

  public async getPermissionsForUser(tenantId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EnumUserPermission)[]> {
    const req = {
    tenantId:tenantId.toString(),
    userId:userId.toString(),
    };
    const extractor = (response:any) => response.permissions.map((v: any) => EnumUserPermission.getByValue(v));;
    return this.makeJsonRequest("user/v1/getPermissionsForUser", req, false, extractor, opts);
  };

  public async localLoginWithEmailAndPassword(email: string, password: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    email:email,
    password:password,
    };
    const extractor = (response:any) => response.sessionToken;;
    return this.makeJsonRequest("user/v1/localLoginWithEmailAndPassword", req, false, extractor, opts);
  };

  public async getUserFavouriteMenus(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(UserFavouriteMenu)[]> {
    const req = {
    };
    const extractor = (response:any) => response.favourites.map((v: any) => new UserFavouriteMenu(v));;
    return this.makeJsonRequest("user/v1/getUserFavouriteMenus", req, false, extractor, opts);
  };

  public async saveUserFavouriteMenus(favourites: readonly UserFavouriteMenu[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    favourites:favourites.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("user/v1/saveUserFavouriteMenus", req, false, extractor, opts);
  };
}

export const userService = new UserService();